import {
  Alert,
  AppShell,
  Avatar,
  Burger,
  Button,
  Group,
  Menu,
  Modal,
  NavLink,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  hasRole,
  useAppVersion,
  useCurrentUser,
  useIstTestsystem,
  useLogout,
} from "../hooks/useAuth.js";
import { useCustomerViewConnection } from "../hooks/useCustomerView.js";
import { useSetOwnName } from "../hooks/useUsers.js";
import { FeedbackButton } from "./FeedbackButton.js";
import { LanguageSwitcher } from "./LanguageSwitcher.js";
import { TestsystemBanner } from "./TestsystemBanner.js";
import { ViewAsBanner } from "./ViewAsBanner.js";
import { ApiTokensCard } from "./profile/ApiTokensCard.js";
import { BetriebPanel } from "./settings/BetriebPanel.js";
import { ViewAsHistoryModal } from "./ViewAsHistoryModal.js";
import { ViewAsPicker } from "./ViewAsPicker.js";
import logoUrl from "../assets/mindsquare-logo.svg";

interface AppLayoutProps {
  children: ReactNode;
}

/** Merkt sich nur, ob die Navigation am Desktop eingeklappt ist — mobil
 * startet die Navigation immer eingeklappt (Overlay), das braucht keinen
 * eigenen Speicher. */
export const NAV_COLLAPSED_KEY = "msc.nav.collapsed";

function readNavCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    // Privater Modus oder Policy kann localStorage sperren — dann bleibt es
    // beim Standard (ausgeklappt).
    return false;
  }
}

function writeNavCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Siehe readNavCollapsed — die Einstellung gilt dann nur für diese
    // Sitzung.
  }
}

export function AppLayout({ children }: AppLayoutProps) {
  const theme = useMantineTheme();
  // Ein Burger fuer beide Breakpoints (statt zwei getrennten): mobil oeffnet
  // er die Navigation als Overlay, am Desktop klappt er sie ein/aus. Welche
  // der beiden Zustaende der Klick trifft, entscheidet die aktuelle
  // Fensterbreite — Mantine blendet ohnehin nur den passenden Navbar-Zustand
  // ein (navbar.collapsed.mobile / .desktop je nach Breakpoint).
  const isMobileViewport = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) ?? false;
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
    useDisclosure(false);
  const [desktopOpened, setDesktopOpened] = useState(() => !readNavCollapsed());
  const [profileOpen, profileHandlers] = useDisclosure(false);
  const [tokensOpen, tokensHandlers] = useDisclosure(false);
  const [betriebOpen, betriebHandlers] = useDisclosure(false);
  const [viewAsOpen, viewAsHandlers] = useDisclosure(false);
  const [viewAsHistoryOpen, viewAsHistoryHandlers] = useDisclosure(false);
  const { user } = useCurrentUser();
  const logout = useLogout();
  const version = useAppVersion();
  const istTestsystem = useIstTestsystem();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { t } = useTranslation();

  // Mobil schliesst ein Klick auf einen Navigationspunkt das Overlay wieder —
  // sonst verdeckt es die Seite, auf die man gerade gewechselt ist. Der
  // Effekt betrifft nur `mobileOpened`; der Desktop-Zustand bleibt unberuehrt.
  useEffect(() => {
    closeMobile();
  }, [currentPath, closeMobile]);

  function toggleNav() {
    if (isMobileViewport) {
      toggleMobile();
      return;
    }
    setDesktopOpened((prev) => {
      const next = !prev;
      writeNavCollapsed(!next);
      return next;
    });
  }

  const isAdmin = hasRole(user, "admin");
  // Wer ueberhaupt eine fremde Sicht uebernehmen kann. Die echte Grenze zieht
  // der Server (viewAs.service.ts) — das hier blendet nur einen Menuepunkt aus,
  // der sonst fuer Projektleiter ins Leere fuehrte.
  const canAssumeViews = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);
  // BCG-Daten sind sensitiv (Umsatz, Wachstum). Nur Rollen mit fachlichem
  // Bezug bekommen den Link zu sehen — siehe canViewSegmentBcg im Backend.
  const canSeeBcg = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
  ]);
  // Freigaben-Inbox und Einstellungen liefern nur für diese Rollen Inhalte
  // (inboxForUser bzw. Schreib-Gates) — für alle anderen wären es tote Türen.
  const canManage = hasRole(user, ["admin", "bereichsleiter", "stellvertreter"]);
  // Service Leads (implizite Rolle `projektleiter`) bekommen ihren eigenen
  // Portfolio-Einstieg, statt ihn im Bereichs-Cockpit suchen zu müssen.
  const isServiceLead = hasRole(user, "projektleiter");
  // Wartungsplanung: Leseansichten sind für dieselben fünf Standardrollen
  // freigegeben wie die Seite selbst (RequireRole in router.ts, Spec §6).
  const canSeeMaintenance = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
  ]);
  // Customer View erscheint nur, wenn der Server sie konfiguriert meldet —
  // sonst führte der Eintrag auf eine Seite, die 503 antwortet.
  const customerView = useCustomerViewConnection();
  const canSeeCustomerView = customerView.data?.configured === true;

  function isActive(to: string, exact = false) {
    if (exact) return currentPath === to;
    return currentPath === to || currentPath.startsWith(`${to}/`);
  }

  // Reihenfolge nach Nutzungshaeufigkeit, nicht nach Entstehungsgeschichte
  // (Rueckmeldung Hendrik, 2026-08-20): Das Bereichs-Cockpit und das eigene
  // Portfolio sind die taeglichen Einstiege und ruecken deshalb direkt hinter
  // das Dashboard — das bleibt der Einstieg, weil es die Seite ist, auf der
  // man nach dem Anmelden landet.
  const navItems = [
    { to: "/", label: t("nav.dashboard"), exact: true },
    ...(canSeeBcg ? [{ to: "/kpi/bereich", label: t("nav.kpi") }] : []),
    ...(isServiceLead
      ? [{ to: "/kpi/portfolio", label: t("nav.kpiPortfolio") }]
      : []),
    { to: "/customers", label: t("nav.customers") },
    { to: "/campaigns", label: t("nav.campaigns") },
    ...(canSeeBcg ? [{ to: "/bcg", label: t("nav.bcg") }] : []),
    ...(canSeeBcg
      ? [{ to: "/kpi/ausreisser", label: t("nav.kpiOutliers") }]
      : []),
    { to: "/statusberichte", label: t("nav.statusReports") },
    { to: "/teamdaily", label: t("nav.teamDaily") },
    { to: "/weekly", label: t("nav.weekly") },
    ...(canSeeCustomerView
      ? [{ to: "/customer-view", label: t("nav.customerView") }]
      : []),
    { to: "/visits", label: t("nav.visits") },
    ...(canSeeMaintenance ? [{ to: "/wartung", label: t("nav.wartung") }] : []),
    ...(canManage ? [{ to: "/inbox", label: t("nav.inbox") }] : []),
    ...(canManage ? [{ to: "/settings", label: t("nav.settings") }] : []),
  ] as const;

  return (
    <AppShell
      // Das Warnband sitzt IM Kopfbereich, nicht im Inhalt: Der Kopf ist fest
      // verankert und bleibt beim Scrollen stehen. Ein Band, das nach der
      // ersten Bildschirmseite verschwindet, hilft genau dann nicht, wenn man
      // tief in einer Maske sitzt und vergessen hat, wo man ist. Der Preis ist
      // diese abhängige Höhe — 60 wie bisher, 86 mit Band.
      header={{ height: istTestsystem ? 86 : 60 }}
      navbar={{
        width: 240,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <TestsystemBanner />
        <Group h={60} px="md" justify="space-between">
          <Group>
            <Burger
              opened={isMobileViewport ? mobileOpened : desktopOpened}
              onClick={toggleNav}
              aria-label={t("nav.toggle")}
              size="sm"
            />
            <img
              src={logoUrl}
              alt="mindsquare"
              style={{ height: 26, display: "block" }}
            />
            <Text fw={500} size="sm" c="dimmed" visibleFrom="sm">
              {t("app.productName")}
            </Text>
          </Group>
          <Group>
            {/* Der Feedback-Knopf steht vor der Sprachwahl: Er gehört zu dem,
                was man gerade tut, nicht zu den Einstellungen. Er blendet sich
                selbst aus, wenn der Server kein Forgejo-Token hat. */}
            <FeedbackButton />
            <LanguageSwitcher />
            {user && (
              <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton>
                    <Group gap="sm" align="center">
                      <Avatar size="sm" color="blue">
                        {user.fullName
                          .split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()}
                      </Avatar>
                      <Text size="sm">{user.fullName}</Text>
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>
                    {user.email}
                    {version ? (
                      <Text size="xs" c="dimmed">
                        {t("app.version", { version })}
                      </Text>
                    ) : null}
                  </Menu.Label>
                  <Menu.Item onClick={profileHandlers.open}>
                    {t("auth.editProfile")}
                  </Menu.Item>
                  <Menu.Item onClick={tokensHandlers.open}>
                    {t("apiTokens.menuItem")}
                  </Menu.Item>
                  {/* Für alle: Wer mit dem CRM arbeitet, soll sehen können, ob
                      die nächtlichen Abgleiche gelaufen sind. Nur der Status —
                      die Logs bleiben im Einstellungs-Reiter für Admins. */}
                  <Menu.Item onClick={betriebHandlers.open}>
                    {t("pages.settings.betrieb.menuItem")}
                  </Menu.Item>
                  {canAssumeViews && !user.assumedBy ? (
                    <Menu.Item onClick={viewAsHandlers.open}>
                      {t("viewAs.menuItem")}
                    </Menu.Item>
                  ) : null}
                  {/* Bewusst ohne Rollenbedingung: Wer betroffen sein kann,
                      muss auch nachsehen koennen. */}
                  <Menu.Item onClick={viewAsHistoryHandlers.open}>
                    {t("viewAs.historyTitle")}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    onClick={() => logout.mutate()}
                    disabled={logout.isPending}
                  >
                    {t("auth.logout")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      {/* data-collapsed ist ein reiner Test-Haken: Mantine steuert das
          Ein-/Ausklappen selbst über eingeblendetes CSS (AppShellMediaStyles),
          nicht über ein Attribut am Element — ohne diesen Haken ließe sich
          der Zustand aus dem DOM heraus nicht zusichern. */}
      <AppShell.Navbar
        p="xs"
        data-collapsed={String(isMobileViewport ? !mobileOpened : !desktopOpened)}
      >
        {navItems.map((item) => (
          <div key={item.to}>
            <NavLink
              component={Link}
              to={item.to}
              label={item.label}
              active={isActive(item.to, "exact" in item ? item.exact : false)}
            />
            {item.to === "/settings" && isAdmin && (
              <NavLink
                component={Link}
                to="/settings/users"
                label={t("nav.settingsUsers")}
                pl="lg"
                active={isActive("/settings/users", true)}
              />
            )}
          </div>
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <ViewAsBanner />
        {children}
      </AppShell.Main>

      <OwnProfileModal opened={profileOpen} onClose={profileHandlers.close} />
      <Modal
        opened={tokensOpen}
        onClose={tokensHandlers.close}
        title={t("apiTokens.modalTitle")}
        size="xl"
      >
        <ApiTokensCard />
      </Modal>
      <Modal
        opened={betriebOpen}
        onClose={betriebHandlers.close}
        title={t("pages.settings.betrieb.menuItem")}
        size="xl"
      >
        <BetriebPanel admin={false} />
      </Modal>
      <ViewAsPicker opened={viewAsOpen} onClose={viewAsHandlers.close} />
      <ViewAsHistoryModal
        opened={viewAsHistoryOpen}
        onClose={viewAsHistoryHandlers.close}
      />
    </AppShell>
  );
}

function OwnProfileModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { user } = useCurrentUser();
  const setName = useSetOwnName();
  const [name, setName_] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (opened) {
      setName_(user?.fullName ?? "");
      setError(null);
    }
  }, [opened, user?.fullName]);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("pages.profile.nameError"));
      return;
    }
    if (trimmed === user?.fullName) {
      handleClose();
      return;
    }
    try {
      await setName.mutateAsync({ fullName: trimmed });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.profile.saveError"));
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t("pages.profile.title")} size="sm" centered>
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          {user?.email}
        </Text>
        <TextInput
          label={t("pages.profile.nameLabel")}
          value={name}
          onChange={(e) => setName_(e.currentTarget.value)}
          autoFocus
          required
        />
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={setName.isPending}>
            {t("common.cancel")}
          </Button>
          <Button loading={setName.isPending} onClick={() => void handleSubmit()}>
            {t("pages.profile.saveButton")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
